import React, { useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

// Base selector
const selectRoomData = (state) => state.room.roomData;

// Composed selector for Timer
const selectTimerState = createSelector([selectRoomData], (roomData) => ({
  startedAt: roomData?.startedAt,
}));

/**
 * Timer component that displays hours:minutes:seconds
 * and handles its own state updates to prevent parent re-renders
 *
 * Can either receive startedAt as a prop or read it from Redux state
 */
function Timer({ startedAt: propStartedAt, sx = {} }) {
  // Use the memoized selector from Redux Toolkit
  const { startedAt: reduxStartedAt } = useSelector(selectTimerState);

  // Use prop value if provided, otherwise use value from Redux
  const startedAt = propStartedAt || reduxStartedAt;

  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!startedAt) {
      return;
    }

    // Initial calculation
    setDuration(Date.now() - startedAt);

    // Set up interval for updates
    const interval = setInterval(() => {
      setDuration(Date.now() - startedAt);
    }, 1000);

    // Clean up interval on unmount or when startedAt changes
    return () => {
      clearInterval(interval);
    };
  }, [startedAt]);

  // Calculate time values
  const hours = Math.floor(duration / 1000 / 60 / 60);
  const minutes = Math.floor(duration / 1000 / 60) - hours * 60;
  const seconds = Math.floor(duration / 1000) - hours * 60 * 60 - minutes * 60;

  // Format the time string
  const timeString = `${hours > 0 ? `${hours}:` : ''}${
    minutes < 10 ? `0${minutes}` : minutes
  }:${seconds < 10 ? `0${seconds}` : seconds}`;

  return <Typography sx={{ ...sx }}>{timeString}</Typography>;
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(Timer);
